import {
    Bar,
    BarChart,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip as RechartsTooltip,
    XAxis,
    YAxis,
} from 'recharts';

import {
    SOURCE_CHART_COLORS,
    SOURCE_LABELS,
} from '@apps/web/features/admin_agent_notes/ui/note_status';
import {
    type AdminAgentNoteOrgVolumeRead,
    type components,
} from '@libraries/typescript/api_client';

type AgentNoteSource = components['schemas']['AgentNoteSource'];

const NAME_TICK_MAX_CHARS = 22;
const ROW_HEIGHT_PX = 28;

function truncateName(name: string): string {
    return name.length > NAME_TICK_MAX_CHARS
        ? `${name.slice(0, NAME_TICK_MAX_CHARS - 1)}…`
        : name;
}

/**
 * Horizontal org-volume bars, stacked by source — one row per live org with
 * notes in the window. Horizontal keeps long agency names readable and scales
 * to any number of orgs without the x-axis label overlap of vertical columns.
 */
export function OrgVolumeChart({
    entries,
    emptyMessage,
    onSelectOrganization,
}: {
    readonly entries: ReadonlyArray<AdminAgentNoteOrgVolumeRead>;
    readonly emptyMessage: string;
    readonly onSelectOrganization: (organizationId: string) => void;
}) {
    if (entries.length === 0) {
        return (
            <p className="text-muted-foreground py-6 text-center text-xs">
                {emptyMessage}
            </p>
        );
    }
    const activeSources: Array<AgentNoteSource> = [];
    const data = entries.map((entry) => {
        const row: Record<string, number | string> = {
            organizationId: entry.organization_id,
            name: entry.organization_name,
            total: entry.count,
        };
        for (const sourceCount of entry.source_counts) {
            row[sourceCount.source] = sourceCount.count;
            if (!activeSources.includes(sourceCount.source)) {
                activeSources.push(sourceCount.source);
            }
        }
        return row;
    });
    const chartHeight = Math.max(160, data.length * ROW_HEIGHT_PX + 32);
    return (
        <div className="flex flex-col gap-2">
            <ResponsiveContainer width="100%" height={chartHeight}>
                <BarChart
                    layout="vertical"
                    data={data}
                    margin={{ top: 4, right: 8, bottom: 0, left: 4 }}
                >
                    <CartesianGrid
                        horizontal={false}
                        stroke="var(--border)"
                        strokeDasharray="0"
                    />
                    <XAxis
                        type="number"
                        fontSize={10}
                        tickLine={false}
                        axisLine={false}
                        stroke="var(--muted-foreground)"
                        allowDecimals={false}
                    />
                    <YAxis
                        type="category"
                        dataKey="name"
                        width={150}
                        fontSize={10}
                        tickLine={false}
                        axisLine={false}
                        stroke="var(--muted-foreground)"
                        interval={0}
                        tickFormatter={truncateName}
                    />
                    <RechartsTooltip
                        cursor={{ fill: 'var(--muted)' }}
                        content={({ active, payload }) => {
                            const point = payload?.[0]?.payload as
                                | { name: string; total: number }
                                | undefined;
                            if (!active || point === undefined) {
                                return null;
                            }
                            const parts = (payload ?? [])
                                .filter((item) => Number(item.value) > 0)
                                .map(
                                    (item) =>
                                        `${SOURCE_LABELS[item.dataKey as AgentNoteSource]} ${String(item.value)}`,
                                );
                            return (
                                <div className="bg-popover text-popover-foreground rounded-md border px-2.5 py-1.5 text-xs shadow-sm">
                                    <p className="font-medium">{point.name}</p>
                                    <p className="tabular-nums">
                                        {parts.join(' · ')}
                                    </p>
                                </div>
                            );
                        }}
                    />
                    {activeSources.map((source) => (
                        <Bar
                            isAnimationActive={false}
                            key={source}
                            dataKey={source}
                            stackId="org"
                            fill={SOURCE_CHART_COLORS[source]}
                            stroke="var(--card)"
                            strokeWidth={1}
                            maxBarSize={22}
                            className="cursor-pointer"
                            onClick={(entry) => {
                                const organizationId = (
                                    entry as {
                                        payload?: {
                                            organizationId?: string;
                                        };
                                    }
                                ).payload?.organizationId;
                                if (organizationId) {
                                    onSelectOrganization(organizationId);
                                }
                            }}
                        />
                    ))}
                </BarChart>
            </ResponsiveContainer>
            <SourceLegend sources={activeSources} />
        </div>
    );
}

/** Shared source legend for the stacked charts. */
export function SourceLegend({
    sources,
}: {
    readonly sources: ReadonlyArray<AgentNoteSource>;
}) {
    return (
        <div className="flex flex-wrap gap-x-3 gap-y-1">
            {sources.map((source) => (
                <span
                    key={source}
                    className="text-muted-foreground flex items-center gap-1 text-[11px]"
                >
                    <span
                        aria-hidden="true"
                        className="h-2 w-2 rounded-[2px]"
                        style={{ background: SOURCE_CHART_COLORS[source] }}
                    />
                    {SOURCE_LABELS[source]}
                </span>
            ))}
        </div>
    );
}
